import React, { useMemo, useState } from 'react';
import { v4 as UUID } from 'uuid';
import { SubmitHandler, useForm } from 'react-hook-form';
import classNames from 'classnames';
import styles from './KeywordFormSection.module.scss';
import { validateKeyword } from './validateKeyword';
import {
  FormControl,
  FormLabel,
  Input,
  Button,
  Icon,
  IconList,
  List,
  ListItem,
  Text,
  VisuallyHidden,
} from 'components/core';
import { Inline, Stack } from 'components/layout';
import { HighlightsResult, HighlightType } from 'query/review/Highlights';
import { useHighlights } from 'hooks/reviews/useHighlights';

type FormValues = {
  newKeyword: string;
};

type KeywordFormSectionProps = {
  title: string;
  titleColor: 'success' | 'danger';
  label: string;
  type: HighlightType;
  onHighlightChangeError: () => void;
  onHighlightChangeSuccess?: (highlights: HighlightsResult) => void;
};

const KeywordFormSection = ({
  title,
  titleColor,
  label,
  type,
  onHighlightChangeError,
  onHighlightChangeSuccess,
}: KeywordFormSectionProps): JSX.Element => {
  const describedById = useMemo(() => UUID(), []);
  // Queued deletes must each keep spinning until their own request returns.
  const [pendingKeywords, setPendingKeywords] = useState<string[]>([]);
  const [announcement, setAnnouncement] = useState('');
  const {
    highlights,
    isAddingHighlight,
    addHighlight,
    removeHighlight,
  } = useHighlights();
  const keywords = highlights?.[`${type}Keywords`] ?? [];

  const { register, resetField, handleSubmit, formState } = useForm<FormValues>(
    { defaultValues: { newKeyword: '' } }
  );

  const newKeywordError = formState.errors.newKeyword?.message;

  const isRemoving = (keyword: string) => pendingKeywords.includes(keyword);

  const handleAddKeyword: SubmitHandler<FormValues> = (data, event) => {
    event?.preventDefault();

    const newWord = data.newKeyword.trim();
    if (!newWord) return;

    setAnnouncement('');
    addHighlight(
      { type, keyword: newWord },
      {
        onSuccess: (saved) => {
          onHighlightChangeSuccess?.(saved);
          resetField('newKeyword');
          setAnnouncement(`Added ${newWord}`);
        },
        onError: onHighlightChangeError,
      }
    );
  };

  const handleDeleteKeyword = (keyword: string) => {
    setAnnouncement('');
    setPendingKeywords((pending) => [...pending, keyword]);
    removeHighlight(
      { type, keyword },
      {
        onSuccess: (saved) => {
          onHighlightChangeSuccess?.(saved);
          setAnnouncement(`Removed ${keyword}`);
        },
        onError: onHighlightChangeError,
        onSettled: () =>
          setPendingKeywords((pending) =>
            pending.filter((word) => word !== keyword)
          ),
      }
    );
  };

  return (
    <form onSubmit={handleSubmit(handleAddKeyword)} className={styles.form}>
      <Stack spacing={3} className={styles.formGroup} component="fieldset">
        <Text
          className={styles.formLegend}
          variant={titleColor}
          component="legend"
          size="sm"
        >
          {title}
        </Text>
        <Inline alignY="top" spacing={3} shouldWrap={false}>
          <FormControl error={newKeywordError} className={styles.keywordInput}>
            <FormLabel isVisuallyHidden>{label}</FormLabel>
            <Input
              size="sm"
              aria-describedby={describedById}
              {...register('newKeyword', {
                validate: (value) => validateKeyword(value, keywords),
              })}
            />
            <Text
              size="xs"
              variant="weak"
              id={describedById}
              className={classNames({
                [styles.visuallyHidden]: Boolean(newKeywordError),
              })}
            >
              Add a word or phrase
            </Text>
          </FormControl>

          <Button
            isLoading={isAddingHighlight}
            htmlType="submit"
            type="brand"
            variant="outline"
            aria-label="Add"
            size="sm"
          >
            Add
          </Button>
        </Inline>

        <VisuallyHidden component="div" aria-live="polite" role="status">
          {announcement}
        </VisuallyHidden>

        <Stack spacing={2} component={List} className={styles.highlightList}>
          {keywords.map((keyword) => (
            <Inline
              alignX="left"
              key={keyword}
              spacing={3}
              component={ListItem}
            >
              <Text size="sm" className={styles.highlight} variant="neutral">
                {keyword}
              </Text>
              <Button
                iconOnly
                htmlType="button"
                type="neutral"
                variant="ghost"
                size="sm"
                isLoading={isRemoving(keyword)}
                onClick={() => handleDeleteKeyword(keyword)}
                aria-label={`Delete ${keyword} from ${title.toLowerCase()} words`}
              >
                <Icon size="md" icon={IconList.light.faTrashCan} />
              </Button>
            </Inline>
          ))}
        </Stack>
      </Stack>
    </form>
  );
};

export default KeywordFormSection;
